<template>
  <v-card
    class="provider-brand-card"
    color="background"
    variant="elevated"
    elevation="4"
    @click="emitEdit"
  >
    <div class="provider-rail" :style="{ backgroundColor: brandColor }"></div>

    <div class="provider-shell">
      <div class="provider-header">
        <div class="provider-header-main">
          <div class="provider-avatar">
            <img
              v-if="showStandaloneIcon && iconMode !== 'mono'"
              :src="(icon as ProviderIcon).src"
              :alt="`${displayLabel} icon`"
              class="provider-avatar-image"
              @error="onIconError"
            >
            <span
              v-else-if="showStandaloneIcon"
              class="provider-avatar-mask"
              :style="iconMaskStyle"
              role="img"
              :aria-label="`${displayLabel} icon`"
            ></span>
            <span v-else class="provider-initials" :style="{ backgroundColor: brandColor }">{{ providerInitials }}</span>
          </div>
          <div class="provider-title-wrap">
            <div class="provider-title">{{ displayLabel }}</div>
          </div>
        </div>

        <div class="provider-status-wrap">
          <v-chip size="small" color="warning" variant="tonal" class="provider-status-chip">
            <v-icon icon="mdi-check-circle" size="x-small" start></v-icon>
            Connected
          </v-chip>
        </div>
      </div>

      <div class="provider-stats">
        <div class="provider-stat">
          <div class="provider-stat-k">Models</div>
          <div class="provider-stat-v">{{ llmModels.length }}</div>
        </div>
        <div class="provider-stat">
          <div class="provider-stat-k">Voices</div>
          <div class="provider-stat-v">{{ voiceModels.length }}</div>
        </div>
        <div class="provider-stat">
          <div class="provider-stat-k">Provider</div>
          <div class="provider-stat-v provider-stat-v-label">{{ providerTypeLabel }}</div>
        </div>
        <div class="provider-stat provider-stat-action">
          <v-tooltip text="Edit Provider" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                icon="mdi-pencil-outline"
                size="small"
                variant="plain"
                class="provider-edit-icon"
                @click.stop="emitEdit"
              />
            </template>
          </v-tooltip>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import { ProviderConfig } from '@/types/providers'
import { ProviderInventoryItem, ProviderLLM, ProviderVoice } from '@/types/msq'
import { ProviderIcon, ThemeName, getProviderBrandColor, getProviderIcon } from '@/utils/providerIcons'

@Options({
  props: {
    providerKey: String
  },
  computed: {
    ...mapGetters(['providerConfigs', 'builtInProviders', 'allProviderModels', 'currentTheme'])
  },
  emits: ['edit']
})
export default class ProviderSummaryCard extends Vue {
  providerKey!: string
  providerConfigs!: Record<string, ProviderConfig>
  builtInProviders!: string[]
  allProviderModels!: Record<string, ProviderInventoryItem[]>
  currentTheme!: string
  iconLoadFailed = false

  get providerConfig(): ProviderConfig | null {
    return this.providerConfigs?.[this.providerKey] || null
  }

  get displayName(): string {
    return this.providerConfig?.providerKey || this.providerKey || 'provider'
  }

  get displayLabel(): string {
    if (this.displayName === 'openai') return 'OpenAI'
    if (this.displayName === 'google') return 'Google Gemini'
    if (this.displayName === 'anthropic') return 'Anthropic'
    if (this.displayName === 'mistral') return 'Mistral'
    if (this.displayName === 'cohere') return 'Cohere'
    if (this.displayName === 'groq') return 'Groq'
    if (this.displayName === 'elevenlabs') return 'ElevenLabs'
    if (this.displayName === 'missionsquad') return 'Mission Squad'
    if (this.displayName === 'grok') return 'Grok'
    return this.displayName
  }

  get isCustomProvider(): boolean {
    return !this.builtInProviders.includes(this.displayName)
  }

  get providerTypeLabel(): string {
    return this.isCustomProvider ? 'Custom' : 'Built-in'
  }

  get providerInitials(): string {
    const raw = this.displayLabel.replace(/[^a-zA-Z0-9]/g, '')
    return raw.slice(0, 2).toUpperCase() || 'AI'
  }

  get inventory(): ProviderInventoryItem[] {
    return this.allProviderModels?.[this.providerKey] || []
  }

  get llmModels(): ProviderLLM[] {
    return this.inventory.filter((item): item is ProviderLLM => item.object === 'model')
  }

  get voiceModels(): ProviderVoice[] {
    return this.inventory.filter((item): item is ProviderVoice => item.object === 'voice')
  }

  get brandColor(): string {
    return getProviderBrandColor(this.displayName)
  }

  get icon(): ProviderIcon | null {
    return getProviderIcon(this.displayName, this.currentTheme as ThemeName)
  }

  get iconMode(): ProviderIcon['mode'] | null {
    return this.icon?.mode || null
  }

  get iconMaskStyle(): Record<string, string> {
    const icon = this.icon
    if (!icon) return {}
    const url = `url("${icon.src}")`
    return {
      '-webkit-mask-image': url,
      'mask-image': url,
      backgroundColor: icon.tint || 'currentColor'
    }
  }

  get showStandaloneIcon(): boolean {
    return !!this.icon && this.iconLoadFailed !== true
  }

  emitEdit() {
    this.$emit('edit', this.providerKey)
  }

  onIconError(): void {
    this.iconLoadFailed = true
  }
}
</script>

<style scoped lang="scss">
.provider-brand-card {
  position: relative;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  cursor: pointer;
  overflow: hidden;
}

.provider-rail {
  position: absolute;
  left: 0;
  top: 10px;
  bottom: 10px;
  width: 4px;
  border-radius: 999px;
}

.provider-shell {
  padding: 18px 18px 16px 22px;
  display: flex;
  flex-direction: column;
}

.provider-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.provider-header-main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.provider-avatar {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-on-surface));
}

.provider-avatar-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.provider-avatar-mask {
  display: block;
  width: 100%;
  height: 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.provider-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
}

.provider-title-wrap {
  min-width: 0;
}

.provider-title {
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.provider-status-chip {
  margin-top: 2px;
}

.provider-stats {
  display: flex;
  gap: 24px;
  margin-top: 18px;
  align-items: flex-start;
}

.provider-stat-k {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-bottom: 4px;
}

.provider-stat-v {
  font-size: 1.05rem;
  line-height: 1.1;
  font-weight: 700;
}

.provider-stat-v-label {
  font-size: 0.92rem;
}

.provider-stat-action {
  margin-left: auto;
  align-self: center;
}

.provider-edit-icon {
  opacity: 0;
  transition: opacity 0.18s ease;
}

.provider-brand-card:hover .provider-edit-icon,
.provider-brand-card:focus-within .provider-edit-icon {
  opacity: 1;
}
</style>
